/* global $ */
import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from "../axiosInstance";
import Swal from "sweetalert2";
import Footer from '../components/Footer';

function LeadQueue() {
    const [leadQueues, setLeadQueues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newRecord, setNewRecord] = useState({ name: '', description: '' });
    const [selectedQueue, setSelectedQueue] = useState(null);
    const formRef = useRef(null);

    // Table state
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(100);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    // ─── Filter & Sort ───────────────────────────────────────────────────────────

    const filtered = leadQueues.filter(q =>
        q.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const sorted = React.useMemo(() => {
        let sortable = [...filtered];
        if (sortConfig.key) {
            sortable.sort((a, b) => {
                let aVal = a[sortConfig.key] ?? '';
                let bVal = b[sortConfig.key] ?? '';
                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sortable;
    }, [filtered, sortConfig]);

    const totalItems = sorted.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentItems = sorted.slice(startIndex, startIndex + itemsPerPage);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key === key) return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
        return '';
    };

    useEffect(() => { setCurrentPage(1); }, [searchTerm]);

    // ─── API ─────────────────────────────────────────────────────────────────────

    const fetchLeadQueues = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get('/api/leadqueue');
            setLeadQueues(res.data.data || res.data || []);
        } catch {
            Swal.fire({ icon: 'error', title: 'Error!', text: 'Failed to fetch lead queues' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchLeadQueues(); }, []);

    // ─── Handlers ────────────────────────────────────────────────────────────────

    const handleChange = (e) => {
        setNewRecord(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const resetForm = () => {
        setNewRecord({ name: '', description: '' });
        setSelectedQueue(null);
        if (formRef.current) formRef.current.classList.remove('was-validated');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formRef.current.checkValidity()) {
            formRef.current.classList.add('was-validated');
            return;
        }
        try {
            const user_id = localStorage.getItem('user_id');
            await axiosInstance.post('/api/leadqueue', {
                ...newRecord,
                isactive: true,
                created_by: user_id,
                updated_by: user_id,
            });
            Swal.fire({ icon: 'success', title: 'Created!', text: 'Lead queue added successfully.', timer: 1500, showConfirmButton: false });
            window.$('#add_leadqueue').modal('hide');
            resetForm();
            fetchLeadQueues();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to create lead queue' });
        }
    };

    const handleEditClick = (queue) => {
        setSelectedQueue(queue);
        setNewRecord({ name: queue.name || '', description: queue.description || '' });
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        if (!selectedQueue) return;
        try {
            const user_id = localStorage.getItem('user_id');
            await axiosInstance.put(`/api/leadqueue/${selectedQueue.id}`, {
                ...newRecord,
                updated_by: user_id,
            });
            Swal.fire({ icon: 'success', title: 'Updated!', text: 'Lead queue updated successfully.', timer: 1500, showConfirmButton: false });
            window.$('#edit_leadqueue').modal('hide');
            resetForm();
            fetchLeadQueues();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update lead queue' });
        }
    };

    const handleDelete = async (queue) => {
        const result = await Swal.fire({
            title: 'Delete Lead Queue?',
            text: `Are you sure you want to delete "${queue.name}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete!'
        });
        if (result.isConfirmed) {
            try {
                await axiosInstance.delete(`/api/leadqueue/${queue.id}`);
                Swal.fire({ icon: 'success', title: 'Deleted!', timer: 1500, showConfirmButton: false });
                fetchLeadQueues();
            } catch {
                Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to delete lead queue' });
            }
        }
    };

    // ─── Pagination ───────────────────────────────────────────────────────────────

    const Pagination = () => {
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);
        if (endPage - startPage + 1 < maxVisible) startPage = Math.max(1, endPage - maxVisible + 1);
        const pageNumbers = [];
        for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);

        return (
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                    Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, totalItems)} of {totalItems} entries
                </div>
                <nav>
                    <ul className="pagination mb-0">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(1)}>
                                <i className="fa fa-angle-double-left"></i>
                            </button>
                        </li>
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(p => p - 1)}>Previous</button>
                        </li>
                        {pageNumbers.map(n => (
                            <li key={n} className={`page-item ${n === currentPage ? 'active' : ''}`}>
                                <button className="page-link" onClick={() => setCurrentPage(n)}>{n}</button>
                            </li>
                        ))}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(p => p + 1)}>Next</button>
                        </li>
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(totalPages)}>
                                <i className="fa fa-angle-double-right"></i>
                            </button>
                        </li>
                    </ul>
                </nav>
            </div>
        );
    };

    return (
        <div>
            <div className="page-wrapper">
                <div className="content container-fluid">

                    {/* Page Header */}
                    <div className="page-header">
                        <div className="content-page-header">
                            <h5>Lead Settings</h5>
                            <div className="list-btn">
                                <ul className="filter-list">
                                    <li>
                                        <a className="btn btn-primary" href="#"
                                            data-bs-toggle="modal" data-bs-target="#add_leadqueue"
                                            onClick={resetForm}>
                                            <i className="fa fa-plus-circle me-2"></i>Add Lead Queue
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="card invoices-tabs-card">
                        <div className="invoices-main-tabs">
                            <div className="row align-items-center">
                                <div className="col-lg-12">
                                    <div className="invoices-tabs">
                                        <ul>
                                            <li><a href="/lead-queue" className="active">Lead Queue</a></li>
                                            <li><a href="/user-type">User Type</a></li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card-table">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div className="d-flex align-items-center">
                                            <label className="me-2">Show</label>
                                            <select className="form-select form-select-sm" style={{ width: 'auto' }}
                                                value={itemsPerPage}
                                                onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                                                <option value={100}>100</option>
                                                <option value={10}>10</option>
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                            </select>
                                            <label className="ms-2">entries</label>
                                        </div>
                                        <div>
                                            <label className="me-2">Search:</label>
                                            <input type="text"
                                                className="form-control form-control-sm d-inline-block"
                                                style={{ width: '200px' }}
                                                placeholder="Search lead queues..."
                                                value={searchTerm}
                                                onChange={e => setSearchTerm(e.target.value)} />
                                        </div>
                                    </div>

                                    <div className="table-responsive">
                                        <table className="table table-center table-hover">
                                            <thead className="thead-light">
                                                <tr>
                                                    <th>#</th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>
                                                        Queue Name{getSortIcon('name')}
                                                    </th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('description')}>
                                                        Description{getSortIcon('description')}
                                                    </th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('isactive')}>
                                                        Status{getSortIcon('isactive')}
                                                    </th>
                                                    <th className="no-sort">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading ? (
                                                    <tr>
                                                        <td colSpan="5" className="text-center">
                                                            <div className="spinner-border" role="status"></div>
                                                        </td>
                                                    </tr>
                                                ) : currentItems.length > 0 ? (
                                                    currentItems.map((queue, index) => (
                                                        <tr key={queue.id}>
                                                            <td>{startIndex + index + 1}</td>
                                                            <td>{queue.name || 'N/A'}</td>
                                                            <td>{queue.description || 'N/A'}</td>
                                                            <td>
                                                                <span className={`badge ${queue.isactive ? 'bg-success' : 'bg-danger'}`}>
                                                                    {queue.isactive ? 'Active' : 'Inactive'}
                                                                </span>
                                                            </td>
                                                            <td className="d-flex align-items-center">
                                                                <a className="btn-action-icon bg-success-light me-2"
                                                                    style={{ cursor: 'pointer' }}
                                                                    data-bs-toggle="modal" data-bs-target="#edit_leadqueue"
                                                                    onClick={() => handleEditClick(queue)} title="Edit">
                                                                    <i className="fa fa-edit"></i>
                                                                </a>
                                                                <a className="btn-action-icon bg-danger-light"
                                                                    style={{ cursor: 'pointer' }}
                                                                    onClick={() => handleDelete(queue)} title="Delete">
                                                                    <i className="fa fa-trash-alt"></i>
                                                                </a>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="5" className="text-center">No lead queues found</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    {totalItems > 0 && <Pagination />}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>

            {/* Add Lead Queue Modal */}
            <div className="modal custom-modal fade" id="add_leadqueue" role="dialog">
                <div className="modal-dialog modal-dialog-centered modal-md">
                    <div className="modal-content">
                        <div className="modal-header border-0 pb-0">
                            <div className="form-header modal-header-title text-start mb-0">
                                <h4 className="mb-0">
                                    <i className="fa fa-layer-group me-2 text-primary"></i>Add Lead Queue
                                </h4>
                            </div>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <form ref={formRef} onSubmit={handleSubmit} noValidate>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-lg-12 col-md-12">
                                        <div className="input-block mb-3">
                                            <label>Queue Name <span className="text-danger">*</span></label>
                                            <input type="text" name="name" value={newRecord.name}
                                                onChange={handleChange} className="form-control"
                                                placeholder="e.g. Hot Leads, Cold Leads" required />
                                            <div className="invalid-feedback">Queue name is required.</div>
                                        </div>
                                    </div>
                                    <div className="col-lg-12 col-md-12">
                                        <div className="input-block mb-3">
                                            <label>Description</label>
                                            <textarea name="description" value={newRecord.description}
                                                onChange={handleChange} className="form-control" rows="3"
                                                placeholder="Enter description (optional)"></textarea>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" data-bs-dismiss="modal" className="btn btn-back cancel-btn me-2">
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary paid-continue-btn">
                                    Add Lead Queue
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Edit Lead Queue Modal */}
            <div className="modal custom-modal fade" id="edit_leadqueue" role="dialog">
                <div className="modal-dialog modal-dialog-centered modal-md">
                    <div className="modal-content">
                        <div className="modal-header border-0 pb-0">
                            <div className="form-header modal-header-title text-start mb-0">
                                <h4 className="mb-0">
                                    <i className="fa fa-edit me-2 text-primary"></i>Edit Lead Queue
                                </h4>
                            </div>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <div className="row">
                                <div className="col-lg-12 col-md-12">
                                    <div className="input-block mb-3">
                                        <label>Queue Name <span className="text-danger">*</span></label>
                                        <input type="text" name="name" value={newRecord.name}
                                            onChange={handleChange} className="form-control"
                                            placeholder="e.g. Hot Leads, Cold Leads" />
                                    </div>
                                </div>
                                <div className="col-lg-12 col-md-12">
                                    <div className="input-block mb-3">
                                        <label>Description</label>
                                        <textarea name="description" value={newRecord.description}
                                            onChange={handleChange} className="form-control" rows="3"
                                            placeholder="Enter description (optional)"></textarea>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" data-bs-dismiss="modal" className="btn btn-back cancel-btn me-2">
                                Cancel
                            </button>
                            <button type="button" onClick={handleUpdate} className="btn btn-primary paid-continue-btn">
                                Update Lead Queue
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default LeadQueue;